import React from 'react';
const { useState } = React;
import { fmtDate } from '../diagnostic/shared.jsx';
import { nextAction, sessionProgress, journey } from '../../coachingJourney.js';
import { COACHING_FOCUS, label } from '../../coachingConfig.js';

// The first screen of a process: what is needed to act. Current situation,
// the next action and the journey of sessions, activities and shared
// progress. Agreement, people, program and the rest stay in their tabs.
// Everything here comes from the coach's view of the process (solo coach).

const card = { background: 'var(--globant-card)', border: '1px solid var(--globant-border)', borderRadius: 16, padding: 20 };
const muted = { fontSize: 13, color: 'var(--globant-muted)' };
const FILTERS = [['all', 'Todo'], ['session', 'Sesiones'], ['activity', 'Actividades'], ['progress', 'Avances']];
const MARK = {
  done: { background: 'var(--globant-green)' },
  next: { background: '#FFFFFF', boxShadow: 'inset 0 0 0 3px var(--globant-green)' },
  ahead: { background: '#FFFFFF', boxShadow: 'inset 0 0 0 2px #CFC9BC' },
};

export default function ProcessSummary({ view, onGo, memberName }) {
  const [filter, setFilter] = useState('all');
  const [allAhead, setAllAhead] = useState(false);
  const p = view.process;
  const prog = sessionProgress(view);
  const next = nextAction(view);
  const all = journey(view, memberName).filter(i => filter === 'all' || i.type === filter);
  // What is still ahead: the next two, the rest on request
  const aheadAll = all.filter(i => !i.date);
  const hidden = allAhead ? 0 : Math.max(0, aheadAll.length - 2);
  const items = hidden ? [...all.filter(i => i.date), ...aheadAll.slice(0, 2)] : all;
  const lastShared = [...(view.sharedEntries || [])].sort((a, b) => String(b.sharedAt || b.createdAt).localeCompare(String(a.sharedAt || a.createdAt)))[0];
  // Team: the shared goals; individual: the goals of the person's own agreement
  const goals = p.modality === 'team' ? p.agreement?.sharedGoals || [] : (view.members || []).find(m => m.status !== 'left')?.agreement?.goals || [];
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
        <section style={{ ...card, flex: '1 1 320px' }}>
          <div style={muted}>Situación actual</div>
          <div style={{ fontSize: 18, fontWeight: 700, margin: '2px 0 6px' }}>
            {p.focus ? `Foco ${label(COACHING_FOCUS, p.focus).toLowerCase()}` : 'Foco sin definir'} <span style={{ ...muted, fontWeight: 400 }}>{p.focusAgreed ? '· acordado' : '· propuesto'}</span>
          </div>
          {goals.length > 0 && <div style={{ fontSize: 14, color: 'var(--globant-text-secondary)' }}>Objetivos: {goals.map(g => g.text || g).filter(Boolean).slice(0, 3).join(' · ')}</div>}
          {lastShared && <div style={{ fontSize: 14, color: 'var(--globant-text-secondary)', marginTop: 6 }}>Lo último que compartieron: «{String(lastShared.text || '').slice(0, 140)}{String(lastShared.text || '').length > 140 ? '…' : ''}»</div>}
          <div style={{ marginTop: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 14, flexWrap: 'wrap' }}>
              <span>{prog.total ? `${prog.done} de ${prog.total} sesiones` : 'Sin sesiones todavía'}</span>
              {prog.total > 1 && <span style={muted}>{prog.finished ? 'Todas las sesiones hechas' : prog.halfway ? 'Hito: mitad del proceso' : 'Próximo hito: mitad del proceso'}</span>}
            </div>
            {prog.total > 0 && (
              <div style={{ display: 'flex', gap: 4, marginTop: 6 }} role="progressbar" aria-label="Sesiones hechas" aria-valuemin={0} aria-valuemax={prog.total} aria-valuenow={prog.done}>
                {Array.from({ length: prog.total }, (_, i) => <span key={i} style={{ flex: 1, height: 10, borderRadius: 5, background: i < prog.done ? 'var(--globant-green)' : i === prog.done ? '#9FD3CB' : '#EEEAE2' }} />)}
              </div>
            )}
          </div>
        </section>
        <section style={{ ...card, flex: '1 1 320px', background: 'var(--globant-green)', borderColor: 'var(--globant-green)', color: '#FFFFFF', display: 'grid', gap: 8, alignContent: 'start' }}>
          <div style={{ fontSize: 13, opacity: 0.9 }}>Próxima acción</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{next.title}</div>
          {(next.detail || next.when) && <div style={{ fontSize: 14, opacity: 0.92 }}>{[next.when && fmtDate(next.when, true), next.detail].filter(Boolean).join(' · ')}</div>}
          <div><button type="button" className="action-btn" style={{ background: '#FFFFFF', color: '#155C54', border: 0, minHeight: 44, padding: '0 18px' }} onClick={() => onGo(next.go)}>Abrir</button></div>
        </section>
      </div>

      <section style={card}>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 8, alignItems: 'center', marginBottom: 8 }}>
          <h3 style={{ margin: 0, fontSize: 17 }}>Recorrido</h3>
          <div role="group" aria-label="Filtrar el recorrido" style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {FILTERS.map(([k, l]) => (
              <button key={k} type="button" aria-pressed={filter === k} onClick={() => setFilter(k)}
                style={{ border: `1px solid ${filter === k ? 'var(--globant-green)' : 'var(--globant-border)'}`, background: filter === k ? 'var(--teal-dim)' : '#FFFFFF', color: filter === k ? '#155C54' : 'var(--globant-text-secondary)', borderRadius: 999, padding: '0 12px', minHeight: 36, font: 'inherit', fontSize: 14, cursor: 'pointer' }}>{l}</button>
            ))}
          </div>
        </div>
        {!items.length ? <div style={muted}>Nada todavía.</div> : (
          <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {items.map(i => (
              <li key={i.id} style={{ display: 'flex', gap: 14, padding: '12px 0', borderTop: '1px solid var(--globant-border)' }}>
                <span aria-hidden="true" style={{ width: 14, height: 14, borderRadius: 7, flex: 'none', marginTop: 4, ...MARK[i.state] }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 10px', alignItems: 'baseline' }}>
                    {i.sessionId
                      ? <button type="button" onClick={() => onGo({ tab: 'sessions', sessionId: i.sessionId })} style={{ border: 0, background: 'none', padding: 0, font: 'inherit', fontWeight: 700, color: 'var(--globant-text)', cursor: 'pointer', textAlign: 'left' }}>{i.title}</button>
                      : <b>{i.title}</b>}
                    <span style={muted}>{[i.who, i.date ? fmtDate(i.date) : 'por delante'].filter(Boolean).join(' · ')}</span>
                  </div>
                  {i.detail && <div style={{ fontSize: 14, color: 'var(--globant-text-secondary)' }}>{i.detail}</div>}
                </div>
              </li>
            ))}
          </ol>
        )}
        {hidden > 0 && <button type="button" className="action-btn" style={{ marginTop: 8 }} onClick={() => setAllAhead(true)}>Ver {hidden} más por delante</button>}
      </section>
    </div>
  );
}
